import { useCallback, useEffect, useRef, useState } from "react";
import useWindowDimensions from "../../hooks/useWindowDimensions";

const STORAGE_KEY = "awc_fahmy_button_position";

// Matches StyledButton's 3.5rem height/width in ChatWidget.tsx.
export const FAHMY_BUTTON_SIZE_PX = 56;

const EDGE_GUTTER_PX = 8;
// Clears DailyStatsFooter (position: fixed; bottom: 0; height: 2rem).
const FOOTER_RESERVE_PX = 48;

// Mirrors the activation thresholds in DNDContext.tsx so drag behavior feels
// consistent app-wide: mouse drags immediately after a small move, touch
// needs a deliberate press-and-hold so a scroll swipe isn't hijacked.
const MOUSE_ACTIVATION_DISTANCE_PX = 8;
const TOUCH_ACTIVATION_DELAY_MS = 200;
const TOUCH_ACTIVATION_TOLERANCE_PX = 6;

type StoredPosition = { xRatio: number; yRatio: number };
type Point = { left: number; top: number };

function isValidRatio(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isFinite(value) &&
    value >= 0 &&
    value <= 1
  );
}

function readStoredPosition(): StoredPosition | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (isValidRatio(parsed?.xRatio) && isValidRatio(parsed?.yRatio)) {
      return { xRatio: parsed.xRatio, yRatio: parsed.yRatio };
    }
    return null;
  } catch {
    return null;
  }
}

function writeStoredPosition(position: StoredPosition) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(position));
  } catch {
    // Private mode / storage quota — position just won't persist.
  }
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function clampToViewport(
  left: number,
  top: number,
  vw: number,
  vh: number,
): Point {
  return {
    left: clamp(left, EDGE_GUTTER_PX, vw - FAHMY_BUTTON_SIZE_PX - EDGE_GUTTER_PX),
    top: clamp(
      top,
      EDGE_GUTTER_PX,
      vh - FAHMY_BUTTON_SIZE_PX - EDGE_GUTTER_PX - FOOTER_RESERVE_PX,
    ),
  };
}

type DragGesture = {
  pointerId: number;
  pointerType: string;
  startClientX: number;
  startClientY: number;
  startLeft: number;
  startTop: number;
  currentLeft: number;
  currentTop: number;
  activated: boolean;
  touchTimer: ReturnType<typeof setTimeout> | null;
};

export function useDraggableFahmyButton(enabled: boolean) {
  const { width: vw, height: vh } = useWindowDimensions();
  const [storedPosition, setStoredPosition] = useState<StoredPosition | null>(
    () => readStoredPosition(),
  );
  const [liveOffset, setLiveOffset] = useState<Point | null>(null);
  const elementRef = useRef<HTMLDivElement>(null);
  const gestureRef = useRef<DragGesture | null>(null);
  const justDraggedRef = useRef(false);

  const settledPosition: Point | null = storedPosition
    ? clampToViewport(
        storedPosition.xRatio * vw,
        storedPosition.yRatio * vh,
        vw,
        vh,
      )
    : null;

  const displayPosition: Point | null = liveOffset
    ? clampToViewport(liveOffset.left, liveOffset.top, vw, vh)
    : settledPosition;

  const clearGesture = useCallback(() => {
    const gesture = gestureRef.current;
    if (gesture?.touchTimer) {
      clearTimeout(gesture.touchTimer);
    }
    gestureRef.current = null;
  }, []);

  const activateDrag = useCallback(() => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.activated) return;
    gesture.activated = true;
    if (gesture.touchTimer) {
      clearTimeout(gesture.touchTimer);
      gesture.touchTimer = null;
    }
  }, []);

  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      const gesture = gestureRef.current;
      if (!gesture || gesture.pointerId !== event.pointerId) return;

      const dx = event.clientX - gesture.startClientX;
      const dy = event.clientY - gesture.startClientY;

      if (!gesture.activated) {
        if (gesture.pointerType === "mouse") {
          if (Math.hypot(dx, dy) < MOUSE_ACTIVATION_DISTANCE_PX) return;
          activateDrag();
        } else {
          // Waiting on the hold timer — a bigger move first means this is a
          // scroll attempt, not a drag, so let it go.
          if (Math.hypot(dx, dy) >= TOUCH_ACTIVATION_TOLERANCE_PX) {
            clearGesture();
          }
          return;
        }
      }

      event.preventDefault();
      const next = clampToViewport(
        gesture.startLeft + dx,
        gesture.startTop + dy,
        vw,
        vh,
      );
      gesture.currentLeft = next.left;
      gesture.currentTop = next.top;
      setLiveOffset(next);
    },
    [activateDrag, clearGesture, vw, vh],
  );

  const handlePointerUp = useCallback(
    (event: PointerEvent) => {
      const gesture = gestureRef.current;
      if (!gesture || gesture.pointerId !== event.pointerId) return;

      if (gesture.activated) {
        const ratioPosition: StoredPosition = {
          xRatio: clamp(gesture.currentLeft / vw, 0, 1),
          yRatio: clamp(gesture.currentTop / vh, 0, 1),
        };
        writeStoredPosition(ratioPosition);
        setStoredPosition(ratioPosition);
        setLiveOffset(null);
        justDraggedRef.current = true;
      }
      clearGesture();
    },
    [vw, vh, clearGesture],
  );

  useEffect(() => {
    if (!enabled) return undefined;
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
  }, [enabled, handlePointerMove, handlePointerUp]);

  const onPointerDown = useCallback(
    (event: React.PointerEvent) => {
      if (!enabled) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;

      const rect = elementRef.current?.getBoundingClientRect();
      const startLeft = rect?.left ?? 0;
      const startTop = rect?.top ?? 0;

      const gesture: DragGesture = {
        pointerId: event.pointerId,
        pointerType: event.pointerType,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startLeft,
        startTop,
        currentLeft: startLeft,
        currentTop: startTop,
        activated: false,
        touchTimer: null,
      };

      if (event.pointerType !== "mouse") {
        gesture.touchTimer = setTimeout(
          activateDrag,
          TOUCH_ACTIVATION_DELAY_MS,
        );
      }

      gestureRef.current = gesture;
    },
    [enabled, activateDrag],
  );

  // Fahmy's icon is an <img>; pressing on it starts the browser's native image
  // drag, which fires pointercancel and kills the gesture above.
  const onDragStart = useCallback((event: React.DragEvent) => {
    event.preventDefault();
  }, []);

  return {
    elementRef,
    onPointerDown,
    onDragStart,
    position: displayPosition,
    isDragging: liveOffset !== null,
    justDraggedRef,
  };
}
